// Combat and chase feedback drawn on the HUD canvas: floating damage numbers, a red edge when you're hurt,
// the "last seen" marker while police search, and arrows to sirens beyond the screen edge.
import type { GameEvent, PlayerSnap, VehicleSnap } from '../../common/src/protocol';
import { COLOR, FONT } from './theme';

const F = (px: number) => `${FONT.bodyWeightStrong} ${px}px ${FONT.body}`;
const NUM_LIFE = 0.8, FLASH = 0.15;

export class Feedback {
  private nums: { x: number; y: number; d: number; col: string; t: number; dx: number }[] = [];
  /** npc id -> seconds of white hit flash left (read by the renderer) */
  flash = new Map<number, number>();
  hurtT = 0; hurtAmp = 0;

  onEvent(e: GameEvent, you: number) {
    if (e.k !== 'dmg' || !e.d) return;
    if (e.n !== undefined) this.flash.set(e.n, FLASH);
    if (e.to === you) { this.hurtAmp = Math.min(1, Math.max(this.hurtT > 0 ? this.hurtAmp : 0, 0.35 + e.d / 40)); this.hurtT = 0.45; this.add(e, COLOR.danger); }
    else if (e.by === you) this.add(e, e.n !== undefined ? COLOR.warning : COLOR.text);
  }
  private add(e: GameEvent, col: string) {
    // stack repeated hits on the same spot instead of piling up unreadable numbers
    const near = this.nums.find(n => n.t > NUM_LIFE * 0.6 && n.col === col && Math.hypot(n.x - e.x, n.y - e.y) < 14);
    if (near) { near.d += e.d!; near.t = NUM_LIFE; return; }
    if (this.nums.length > 40) this.nums.shift();
    this.nums.push({ x: e.x, y: e.y - 8, d: e.d!, col, t: NUM_LIFE, dx: (Math.random() - 0.5) * 8 });
  }
  /** ticks timers; the renderer reads `flash` for this frame */
  step(dt: number) {
    for (const [id, t] of this.flash) { if (t - dt <= 0) this.flash.delete(id); else this.flash.set(id, t - dt); }
    this.hurtT = Math.max(0, this.hurtT - dt);
    for (const n of this.nums) n.t -= dt;
    this.nums = this.nums.filter(n => n.t > 0);
  }

  draw(g: CanvasRenderingContext2D, o: {
    W: number; H: number; u: number; w2s: (x: number, y: number) => [number, number]; t: number;
    me: PlayerSnap; myVehicle: number; vehicles: VehicleSnap[]; lowFx: boolean;
  }) {
    const { W, H, u, w2s } = o;
    g.save(); g.textBaseline = 'middle'; g.textAlign = 'center';
    // last place the police saw you, while they search
    if (o.me.ph === 2 && o.me.lx !== undefined && o.me.ly !== undefined) {
      const [x, y] = w2s(o.me.lx, o.me.ly!), r = (7 + Math.sin(o.t * 6) * 1.5) * u;
      if (x > -20 && y > -20 && x < W + 20 && y < H + 20) {
        g.strokeStyle = COLOR.warning + 'd9'; g.lineWidth = Math.max(1, u * 0.8); g.setLineDash([3 * u, 2 * u]);
        g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.stroke(); g.setLineDash([]);
        g.beginPath(); g.moveTo(x - r * 0.5, y - r * 0.5); g.lineTo(x + r * 0.5, y + r * 0.5); g.moveTo(x + r * 0.5, y - r * 0.5); g.lineTo(x - r * 0.5, y + r * 0.5); g.stroke();
        g.font = F(Math.max(10, 5 * u)); g.fillStyle = COLOR.ground + 'aa'; g.fillText('LAST SEEN', x + u * 0.6, y - r - 4 * u + u * 0.6); g.fillStyle = COLOR.warning; g.fillText('LAST SEEN', x, y - r - 4 * u);
      }
    }
    // sirens outside the screen: an arrow on the edge pointing at each, brighter when closer
    const [cx, cy] = [W / 2, H / 2], m = 14 * u;
    for (const v of o.vehicles) {
      if (!(v.f & 1) || v.id === o.myVehicle) continue;
      const [x, y] = w2s(v.x, v.y);
      if (x >= 0 && y >= 0 && x <= W && y <= H) continue;
      const d = Math.hypot(v.x - o.me.x, v.y - o.me.y); if (d > 1100) continue;
      const a = Math.atan2(y - cy, x - cx), k = Math.min((W / 2 - m) / Math.abs(Math.cos(a) || 1e-6), (H / 2 - m) / Math.abs(Math.sin(a) || 1e-6));
      const ex = cx + Math.cos(a) * k, ey = cy + Math.sin(a) * k, s = 6 * u;
      g.globalAlpha = 0.45 + 0.55 * Math.max(0, 1 - d / 1100);
      g.fillStyle = Math.floor(o.t * 6) % 2 ? COLOR.interactive : COLOR.danger;
      g.beginPath(); g.moveTo(ex + Math.cos(a) * s, ey + Math.sin(a) * s);
      g.lineTo(ex + Math.cos(a + 2.5) * s, ey + Math.sin(a + 2.5) * s); g.lineTo(ex + Math.cos(a - 2.5) * s, ey + Math.sin(a - 2.5) * s); g.closePath(); g.fill();
      g.globalAlpha = 1;
    }
    // damage numbers
    for (const n of this.nums) {
      const k = 1 - n.t / NUM_LIFE, [x, y] = w2s(n.x, n.y);
      const yy = y - k * 14 * u, size = Math.max(11, (6 + Math.min(4, n.d / 15)) * u * (k < 0.15 ? 1 + (0.15 - k) * 3 : 1));
      g.globalAlpha = Math.min(1, n.t / (NUM_LIFE * 0.4)); g.font = F(size);
      g.fillStyle = COLOR.ground + 'cc'; g.fillText(String(Math.round(n.d)), x + n.dx * u + u * 0.7, yy + u * 0.7);
      g.fillStyle = n.col; g.fillText(String(Math.round(n.d)), x + n.dx * u, yy);
    }
    g.globalAlpha = 1;
    // red edge when you're hit
    if (this.hurtT > 0) {
      const a = this.hurtAmp * (this.hurtT / 0.45) * (o.lowFx ? 0.6 : 1);
      const gr = g.createRadialGradient(cx, cy, Math.min(W, H) * 0.35, cx, cy, Math.hypot(W, H) / 2);
      gr.addColorStop(0, COLOR.danger + '00'); gr.addColorStop(1, COLOR.danger + Math.round(0.55 * a * 255).toString(16).padStart(2, '0'));
      g.fillStyle = gr; g.fillRect(0, 0, W, H);
    }
    g.restore();
  }
}
